import _extends from '@babel/runtime/helpers/extends';
import * as React from 'react';
import { View, Id } from '@khanacademy/wonder-blocks-core';
import { mergeTheme, createThemeContext, ThemeSwitcherContext, useScopedTheme, useStyles, withScopedTheme } from '@khanacademy/wonder-blocks-theming';
import { semanticColor, border, spacing } from '@khanacademy/wonder-blocks-tokens';
import { HeadingMedium, LabelSmall } from '@khanacademy/wonder-blocks-typography';
import * as ReactDOM from 'react-dom';
import { StyleSheet } from 'aphrodite';
import { withActionScheduler } from '@khanacademy/wonder-blocks-timing';
import { actionStyles, focusStyles } from '@khanacademy/wonder-blocks-styles';
import xIcon from '@phosphor-icons/core/regular/x.svg';
import IconButton from '@khanacademy/wonder-blocks-icon-button';
import { MediaLayout } from '@khanacademy/wonder-blocks-layout';

const theme$1 = {
  root: {
    color: {
      inverse: {
        background: semanticColor.surface.inverse,
        foreground: semanticColor.text.inverse
      }
    },
    border: {
      radius: border.radius.radius_040
    }
  },
  backdrop: {
    color: {
      background: semanticColor.surface.overlay
    }
  },
  dialog: {
    spacing: {
      padding: spacing.medium_16
    }
  },
  footer: {
    color: {
      border: semanticColor.border.primary
    }
  },
  header: {
    color: {
      border: semanticColor.border.primary,
      secondary: semanticColor.text.secondary
    },
    spacing: {
      paddingBlockMd: spacing.large_24,
      paddingInlineMd: spacing.xLarge_32,
      paddingInlineSm: spacing.medium_16,
      gap: spacing.xSmall_8,
      titleGapMd: spacing.medium_16,
      titleGapSm: spacing.xLarge_32
    }
  },
  panel: {
    spacing: {
      gap: spacing.xLarge_32
    }
  },
  closeButton: {
    spacing: {
      gap: spacing.xSmall_8
    }
  }
};

const theme = mergeTheme(theme$1, {
  root: {
    color: {
      inverse: {
        background: semanticColor.khanmigo.primary
      }
    }
  }
});

const themes = {
  default: theme$1,
  khanmigo: theme
};
const ModalDialogThemeContext = createThemeContext(theme$1);
function ThemeModalDialog(props) {
  const currentTheme = React.useContext(ThemeSwitcherContext);
  const theme = themes[currentTheme] || theme$1;
  return React.createElement(ModalDialogThemeContext.Provider, {
    value: theme
  }, props.children);
}

const ModalDialogCore = React.forwardRef(function ModalDialogCore(props, ref) {
  const {
    above,
    below,
    role = "dialog",
    style,
    children,
    testId,
    "aria-labelledby": ariaLabelledBy,
    "aria-describedby": ariaDescribedBy
  } = props;
  const {
    theme
  } = useScopedTheme(ModalDialogThemeContext);
  const styles = useStyles(themedStylesFn$5, theme);
  return React.createElement(View, {
    style: [styles.wrapper, style]
  }, below && React.createElement(View, {
    style: styles.below
  }, below), React.createElement(View, {
    role: role,
    "aria-modal": "true",
    "aria-labelledby": ariaLabelledBy,
    "aria-describedby": ariaDescribedBy,
    ref: ref,
    style: styles.dialog,
    testId: testId
  }, children), above && React.createElement(View, {
    style: styles.above
  }, above));
});
const ModalDialog = React.forwardRef(function ModalDialog(props, ref) {
  return React.createElement(ThemeModalDialog, null, React.createElement(ModalDialogCore, _extends({}, props, {
    ref: ref
  })));
});
const small$2 = "@media (max-width: 767px)";
const themedStylesFn$5 = theme => ({
  wrapper: {
    display: "flex",
    flexDirection: "row",
    alignItems: "stretch",
    width: "100%",
    height: "100%",
    position: "relative",
    [small$2]: {
      padding: theme.dialog.spacing.padding,
      flexDirection: "column"
    }
  },
  dialog: {
    width: "100%",
    height: "100%",
    borderRadius: theme.root.border.radius,
    overflow: "hidden"
  },
  above: {
    pointerEvents: "none",
    position: "absolute",
    top: 0,
    left: 0,
    bottom: 0,
    right: 0,
    zIndex: 1
  },
  below: {
    pointerEvents: "none",
    position: "absolute",
    top: 0,
    left: 0,
    bottom: 0,
    right: 0,
    zIndex: -1
  }
});
ModalDialog.displayName = "ModalDialog";

function ModalFooter({
  children
}) {
  const {
    theme
  } = useScopedTheme(ModalDialogThemeContext);
  const styles = useStyles(themedStylesFn$4, theme);
  return React.createElement(View, {
    style: styles.footer
  }, children);
}
ModalFooter.__IS_MODAL_FOOTER__ = true;
ModalFooter.isComponentOf = instance => {
  return instance && instance.type && instance.type.__IS_MODAL_FOOTER__;
};
const themedStylesFn$4 = theme => ({
  footer: {
    flex: "0 0 auto",
    boxSizing: "border-box",
    minHeight: spacing.xxxLarge_64,
    paddingLeft: spacing.medium_16,
    paddingRight: spacing.medium_16,
    paddingTop: spacing.xSmall_8,
    paddingBottom: spacing.xSmall_8,
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-end",
    boxShadow: `0px -1px 0px ${theme.footer.color.border}`
  }
});

function ModalHeader(props) {
  const {
    breadcrumbs = undefined,
    light,
    subtitle = undefined,
    testId,
    title,
    titleId
  } = props;
  if (subtitle && breadcrumbs) {
    throw new Error("'subtitle' and 'breadcrumbs' can't be used together");
  }
  const {
    theme
  } = useScopedTheme(ModalDialogThemeContext);
  const styles = useStyles(themedStylesFn$3, theme);
  return React.createElement(View, {
    style: [styles.header, !light && styles.dark],
    testId: testId
  }, breadcrumbs && React.createElement(View, {
    style: styles.breadcrumbs
  }, breadcrumbs), React.createElement(HeadingMedium, {
    tag: "h2",
    style: styles.title,
    id: titleId,
    testId: testId && `${testId}-title`
  }, title), subtitle && React.createElement(LabelSmall, {
    style: light && styles.subtitle,
    testId: testId && `${testId}-subtitle`
  }, subtitle));
}
const small$1 = "@media (max-width: 767px)";
const themedStylesFn$3 = theme => ({
  header: {
    boxShadow: `0px 1px 0px ${theme.header.color.border}`,
    display: "flex",
    flexDirection: "column",
    minHeight: 66,
    paddingBlock: theme.header.spacing.paddingBlockMd,
    paddingInline: theme.header.spacing.paddingInlineMd,
    position: "relative",
    width: "100%",
    [small$1]: {
      paddingInline: theme.header.spacing.paddingInlineSm
    }
  },
  dark: {
    background: theme.root.color.inverse.background,
    color: theme.root.color.inverse.foreground
  },
  breadcrumbs: {
    color: theme.header.color.secondary,
    marginBottom: theme.header.spacing.gap
  },
  title: {
    paddingRight: theme.header.spacing.titleGapMd,
    [small$1]: {
      paddingRight: theme.header.spacing.titleGapSm
    }
  },
  subtitle: {
    color: theme.header.color.secondary,
    marginTop: theme.header.spacing.gap
  }
});
ModalHeader.defaultProps = {
  light: true
};

const FOCUSABLE_ELEMENTS$1 = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
class FocusTrap extends React.Component {
  constructor(...args) {
    super(...args);
    this.modalRoot = void 0;
    this.getModalRoot = node => {
      if (!node) {
        return;
      }
      const modalRoot = ReactDOM.findDOMNode(node);
      if (!modalRoot) {
        throw new Error("Assertion error: modal root should exist after mount");
      }
      this.modalRoot = modalRoot;
    };
    this.handleFocusMoveToLast = () => {
      this.focusElementIn(false);
    };
    this.handleFocusMoveToFirst = () => {
      this.focusElementIn(true);
    };
  }
  tryToFocus(node) {
    if (node instanceof HTMLElement) {
      try {
        node.focus();
      } catch (e) {}
      return document.activeElement === node;
    }
  }
  focusElementIn(isLast) {
    const modalRootAsHtmlEl = this.modalRoot;
    const focusableNodes = Array.from(modalRootAsHtmlEl.querySelectorAll(FOCUSABLE_ELEMENTS$1));
    const nodeIndex = !isLast ? focusableNodes.length - 1 : 0;
    const focusableNode = focusableNodes[nodeIndex];
    this.tryToFocus(focusableNode);
  }
  render() {
    const {
      style
    } = this.props;
    return React.createElement(React.Fragment, null, React.createElement("div", {
      tabIndex: 0,
      className: "modal-focus-trap-first",
      onFocus: this.handleFocusMoveToLast,
      style: {
        position: "fixed"
      }
    }), React.createElement(View, {
      style: style,
      ref: this.getModalRoot
    }, this.props.children), React.createElement("div", {
      tabIndex: 0,
      className: "modal-focus-trap-last",
      onFocus: this.handleFocusMoveToFirst,
      style: {
        position: "fixed"
      }
    }));
  }
}

const ModalLauncherPortalAttributeName = "data-modal-launcher-portal";

const FOCUSABLE_ELEMENTS = "a[href], details, input, textarea, select, button";
function findFocusableNodes(root) {
  return Array.from(root.querySelectorAll(FOCUSABLE_ELEMENTS));
}

class ModalBackdrop extends React.Component {
  constructor(...args) {
    super(...args);
    this._mousePressedOutside = false;
    this.handleMouseDown = e => {
      this._mousePressedOutside = e.target === e.currentTarget;
    };
    this.handleMouseUp = e => {
      if (e.target === e.currentTarget && this._mousePressedOutside) {
        this.props.onCloseModal();
      }
      this._mousePressedOutside = false;
    };
  }
  componentDidMount() {
    const node = ReactDOM.findDOMNode(this);
    if (!node) {
      return;
    }
    const firstFocusableElement = this._getInitialFocusElement(node) || this._getFirstFocusableElement(node) || this._getDialogElement(node);
    setTimeout(() => {
      firstFocusableElement.focus();
    }, 0);
  }
  _getInitialFocusElement(node) {
    const {
      initialFocusId
    } = this.props;
    if (!initialFocusId) {
      return null;
    }
    return ReactDOM.findDOMNode(node.querySelector(`#${initialFocusId}`));
  }
  _getFirstFocusableElement(node) {
    const focusableElements = findFocusableNodes(node);
    if (!focusableElements) {
      return null;
    }
    return focusableElements[0];
  }
  _getDialogElement(node) {
    const dialogElement = ReactDOM.findDOMNode(node.querySelector('[role="dialog"]'));
    dialogElement.tabIndex = -1;
    return dialogElement;
  }
  render() {
    const {
      children,
      testId
    } = this.props;
    const backdropProps = {
      [ModalLauncherPortalAttributeName]: true
    };
    return React.createElement(View, _extends({
      style: this.props.wbThemeStyles.modalPositioner,
      onMouseDown: this.handleMouseDown,
      onMouseUp: this.handleMouseUp,
      testId: testId
    }, backdropProps), children);
  }
}
const themedStylesFn$2 = theme => ({
  modalPositioner: {
    position: "fixed",
    left: 0,
    top: 0,
    width: "100%",
    height: "100%",
    alignItems: "center",
    justifyContent: "center",
    overflow: "auto",
    background: theme.backdrop.color.background
  }
});
var ModalBackdrop$1 = withScopedTheme(themedStylesFn$2, ModalDialogThemeContext)(ModalBackdrop);

const needsHackyMobileSafariScrollDisabler = (() => {
  if (typeof window === "undefined") {
    return false;
  }
  const userAgent = window.navigator.userAgent;
  return userAgent.indexOf("iPad") > -1 || userAgent.indexOf("iPhone") > -1;
})();
class ScrollDisabler extends React.Component {
  componentDidMount() {
    if (ScrollDisabler.numModalsOpened === 0) {
      const body = document.body;
      if (!body) {
        throw new Error("couldn't find document.body");
      }
      ScrollDisabler.oldOverflow = body.style.overflow;
      ScrollDisabler.oldScrollY = window.scrollY;
      if (needsHackyMobileSafariScrollDisabler) {
        ScrollDisabler.oldPosition = body.style.position;
        ScrollDisabler.oldWidth = body.style.width;
        ScrollDisabler.oldTop = body.style.top;
      }
      body.style.overflow = "hidden";
      if (needsHackyMobileSafariScrollDisabler) {
        body.style.position = "fixed";
        body.style.width = "100%";
        body.style.top = `${-ScrollDisabler.oldScrollY}px`;
      }
    }
    ScrollDisabler.numModalsOpened++;
  }
  componentWillUnmount() {
    ScrollDisabler.numModalsOpened--;
    if (ScrollDisabler.numModalsOpened === 0) {
      const body = document.body;
      if (!body) {
        throw new Error("couldn't find document.body");
      }
      body.style.overflow = ScrollDisabler.oldOverflow;
      if (needsHackyMobileSafariScrollDisabler) {
        body.style.position = ScrollDisabler.oldPosition;
        body.style.width = ScrollDisabler.oldWidth;
        body.style.top = ScrollDisabler.oldTop;
      }
      if (typeof window !== "undefined" && window.scrollTo) {
        window.scrollTo(0, ScrollDisabler.oldScrollY);
      }
    }
  }
  render() {
    return null;
  }
}
ScrollDisabler.oldOverflow = void 0;
ScrollDisabler.oldPosition = void 0;
ScrollDisabler.oldScrollY = void 0;
ScrollDisabler.oldWidth = void 0;
ScrollDisabler.oldTop = void 0;
ScrollDisabler.numModalsOpened = 0;

const defaultContext = {
  closeModal: undefined
};
const ModalContext = React.createContext(defaultContext);
ModalContext.displayName = "ModalContext";

class ModalLauncher extends React.Component {
  constructor(...args) {
    super(...args);
    this.lastElementFocusedOutsideModal = void 0;
    this.state = {
      opened: false
    };
    this._saveLastElementFocused = () => {
      this.lastElementFocusedOutsideModal = document.activeElement;
    };
    this._openModal = () => {
      this._saveLastElementFocused();
      this.setState({
        opened: true
      });
    };
    this._returnFocus = () => {
      const {
        closedFocusId,
        schedule
      } = this.props;
      const lastElement = this.lastElementFocusedOutsideModal;
      if (closedFocusId) {
        const focusElement = ReactDOM.findDOMNode(document.getElementById(closedFocusId));
        if (focusElement) {
          schedule.animationFrame(() => {
            focusElement.focus();
          });
          return;
        }
      }
      if (lastElement != null) {
        schedule.animationFrame(() => {
          lastElement.focus();
        });
      }
    };
    this.handleCloseModal = () => {
      this.setState({
        opened: false
      }, () => {
        const {
          onClose
        } = this.props;
        onClose == null || onClose();
        this._returnFocus();
      });
    };
  }
  static getDerivedStateFromProps(props, state) {
    if (typeof props.opened === "boolean" && props.children) {
      console.warn("'children' and 'opened' can't be used together");
    }
    if (typeof props.opened === "boolean" && !props.onClose) {
      console.warn("'onClose' should be used with 'opened'");
    }
    if (typeof props.opened !== "boolean" && !props.children) {
      console.warn("either 'children' or 'opened' must be set");
    }
    return {
      opened: typeof props.opened === "boolean" ? props.opened : state.opened
    };
  }
  componentDidUpdate(prevProps) {
    if (!prevProps.opened && this.props.opened) {
      this._saveLastElementFocused();
    }
  }
  _renderModal() {
    if (typeof this.props.modal === "function") {
      return this.props.modal({
        closeModal: this.handleCloseModal
      });
    } else {
      return this.props.modal;
    }
  }
  render() {
    const renderedChildren = this.props.children ? this.props.children({
      openModal: this._openModal
    }) : null;
    const {
      body
    } = document;
    if (!body) {
      return null;
    }
    return React.createElement(ModalContext.Provider, {
      value: {
        closeModal: this.handleCloseModal
      }
    }, renderedChildren, this.state.opened && ReactDOM.createPortal(React.createElement(FocusTrap, {
      style: styles.container
    }, React.createElement(ModalBackdrop$1, {
      initialFocusId: this.props.initialFocusId,
      testId: this.props.testId,
      onCloseModal: this.props.backdropDismissEnabled ? this.handleCloseModal : () => {}
    }, this._renderModal())), body), this.state.opened && React.createElement(ModalLauncherKeypressListener, {
      onClose: this.handleCloseModal
    }), this.state.opened && React.createElement(ScrollDisabler, null));
  }
}
ModalLauncher.defaultProps = {
  backdropDismissEnabled: true
};
class ModalLauncherKeypressListener extends React.Component {
  constructor(...args) {
    super(...args);
    this._handleKeyup = e => {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        this.props.onClose();
      }
    };
  }
  componentDidMount() {
    window.addEventListener("keyup", this._handleKeyup);
  }
  componentWillUnmount() {
    window.removeEventListener("keyup", this._handleKeyup);
  }
  render() {
    return null;
  }
}
const styles = StyleSheet.create({
  container: {
    zIndex: 1080
  }
});
var modalLauncher = withActionScheduler(ModalLauncher);

function ModalContent(props) {
  const {
    scrollOverflow,
    style,
    children
  } = props;
  const {
    theme
  } = useScopedTheme(ModalDialogThemeContext);
  const styles = useStyles(themedStylesFn$1, theme);
  return React.createElement(View, {
    style: [styles.wrapper, scrollOverflow && styles.scrollOverflow]
  }, React.createElement(View, {
    style: [styles.content, style]
  }, children));
}
ModalContent.__IS_MODAL_CONTENT__ = true;
ModalContent.isComponentOf = instance => {
  return instance && instance.type && instance.type.__IS_MODAL_CONTENT__;
};
const small = "@media (max-width: 767px)";
const themedStylesFn$1 = theme => ({
  wrapper: {
    flex: 1,
    display: "block"
  },
  scrollOverflow: {
    overflow: "auto"
  },
  content: {
    flex: 1,
    minHeight: "100%",
    padding: spacing.xLarge_32,
    boxSizing: "border-box",
    [small]: {
      padding: `${spacing.xLarge_32}px ${spacing.medium_16}px`
    }
  }
});
ModalContent.defaultProps = {
  scrollOverflow: true
};

class CloseButton extends React.Component {
  render() {
    const {
      onClick,
      style,
      testId
    } = this.props;
    return React.createElement(ModalContext.Consumer, null, ({
      closeModal
    }) => {
      if (closeModal && onClick) {
        throw new Error("You've specified 'onClose' on a modal when using ModalLauncher.  Please specify 'onClose' on the ModalLauncher instead");
      }
      return React.createElement(IconButton, {
        icon: xIcon,
        "aria-label": "Close modal",
        onClick: onClick || closeModal,
        kind: "tertiary",
        actionType: "neutral",
        style: style,
        testId: testId
      });
    });
  }
}

function ModalPanel({
  closeButtonVisible = true,
  scrollOverflow = true,
  light = true,
  content,
  footer,
  header,
  onClose,
  style,
  testId
}) {
  const {
    theme
  } = useScopedTheme(ModalDialogThemeContext);
  const styles = useStyles(themedStylesFn, theme);
  const renderMainContent = React.useCallback(() => {
    const mainContent = ModalContent.isComponentOf(content) ? content : React.createElement(ModalContent, null, content);
    if (!mainContent) {
      return mainContent;
    }
    return React.cloneElement(mainContent, {
      scrollOverflow,
      style: [!!footer && styles.hasFooter, mainContent.props.style]
    });
  }, [content, footer, scrollOverflow, styles.hasFooter]);
  const mainContent = renderMainContent();
  const isInverse = !light;
  return React.createElement(View, {
    style: [styles.wrapper, isInverse && styles.dark, style],
    testId: testId && `${testId}-panel`
  }, closeButtonVisible && React.createElement(CloseButton, {
    onClick: onClose,
    style: [styles.closeButton, isInverse && actionStyles.inverse],
    testId: testId && `${testId}-close`
  }), header, mainContent, !footer || ModalFooter.isComponentOf(footer) ? footer : React.createElement(ModalFooter, null, footer));
}
ModalPanel.defaultProps = {
  closeButtonVisible: true,
  scrollOverflow: true,
  light: true
};
const themedStylesFn = theme => ({
  wrapper: {
    flex: "1 1 auto",
    position: "relative",
    display: "flex",
    flexDirection: "column",
    background: "white",
    boxSizing: "border-box",
    overflow: "hidden",
    height: "100%",
    width: "100%"
  },
  closeButton: {
    position: "absolute",
    right: theme.closeButton.spacing.gap,
    top: theme.closeButton.spacing.gap,
    zIndex: 1,
    ":focus": _extends({}, focusStyles.focus[":focus-visible"])
  },
  dark: {
    background: theme.root.color.inverse.background,
    color: theme.root.color.inverse.foreground
  },
  hasFooter: {
    paddingBlockEnd: theme.panel.spacing.gap
  }
});

class OnePaneDialog extends React.Component {
  renderHeader(uniqueId) {
    const {
      title,
      breadcrumbs = undefined,
      subtitle = undefined,
      testId
    } = this.props;
    if (breadcrumbs) {
      return React.createElement(ModalHeader, {
        title: title,
        breadcrumbs: breadcrumbs,
        titleId: uniqueId,
        testId: testId && `${testId}-header`
      });
    } else if (subtitle) {
      return React.createElement(ModalHeader, {
        title: title,
        subtitle: subtitle,
        titleId: uniqueId,
        testId: testId && `${testId}-header`
      });
    } else {
      return React.createElement(ModalHeader, {
        title: title,
        titleId: uniqueId,
        testId: testId && `${testId}-header`
      });
    }
  }
  render() {
    const {
      onClose,
      footer,
      content,
      above,
      below,
      style,
      closeButtonVisible,
      testId,
      titleId,
      role,
      "aria-describedby": ariaDescribedBy
    } = this.props;
    return React.createElement(MediaLayout, {
      styleSheets: styleSheets
    }, ({
      styles
    }) => React.createElement(Id, {
      id: titleId
    }, uniqueId => React.createElement(ModalDialog, {
      style: [styles.dialog, style],
      above: above,
      below: below,
      testId: testId,
      "aria-labelledby": uniqueId,
      "aria-describedby": ariaDescribedBy,
      role: role
    }, React.createElement(ModalPanel, {
      onClose: onClose,
      header: this.renderHeader(uniqueId),
      content: content,
      footer: footer,
      closeButtonVisible: closeButtonVisible,
      testId: testId
    }))));
  }
}
OnePaneDialog.defaultProps = {
  closeButtonVisible: true
};
const styleSheets = {
  small: StyleSheet.create({
    dialog: {
      width: "100%",
      height: "100%",
      overflow: "hidden"
    }
  }),
  mdOrLarger: StyleSheet.create({
    dialog: {
      width: "93.75%",
      maxWidth: 576,
      height: "81.25%",
      maxHeight: 624
    }
  })
};

function maybeGetNextAncestorModalLauncherPortal(element) {
  let candidateElement = element && element.parentElement;
  while (candidateElement && !candidateElement.hasAttribute(ModalLauncherPortalAttributeName)) {
    candidateElement = candidateElement.parentElement;
  }
  return candidateElement;
}
function maybeGetPortalMountedModalHostElement(element) {
  return maybeGetNextAncestorModalLauncherPortal(element);
}

export { ModalDialog, ModalFooter, ModalHeader, modalLauncher as ModalLauncher, ModalPanel, OnePaneDialog, maybeGetPortalMountedModalHostElement };
